import { FOOTER_LINKS } from "../../data/home";
import type { FooterQuickLink } from "../../types";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-ink-2 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-5 lg:px-16">
        {/* Top grid — stacked on mobile → 4 col on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 py-16 border-b border-white/[0.07]">
          {/* Brand column */}
          <div className="md:col-span-1">
            <span className="font-syne font-extrabold text-[1.5rem] tracking-tight mb-4 block">
              <span className="text-white/85">Ol</span>
              <span className="text-gold">ó</span>
              <span className="text-violet-light">ye</span>
              {' '}
              <span className="text-white/85">Ev</span>
              <span className="text-violet-light">en</span>
              <span className="text-gold">ts</span>
            </span>
            <p className="text-white/25 text-xs font-light leading-[1.75] max-w-[220px]">
              Professional balloon design, custom backdrops, and large-format printing for events across NYC and New Jersey.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {["Woman-Owned", "Minority-Owned", "SAM.gov Registered", "Fully Insured"].map((badge) => (
                <span
                  key={badge}
                  className="bg-gold/10 border border-gold/20 text-gold font-syne text-[8.5px] font-bold tracking-[0.14em] uppercase px-3 py-1 rounded-sm"
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>

          {/* Services */}
          <FooterColumn title="Services" items={FOOTER_LINKS.services} />

          {/* Clients */}
          <FooterColumn title="Clients" items={FOOTER_LINKS.clients} highlightIndexes={[2, 3]} />

          {/* Quick Links */}
          <div>
            <h5 className="font-syne text-[9px] font-bold tracking-[0.22em] uppercase text-gold mb-5">
              Quick Links
            </h5>
            <ul className="space-y-2.5">
              {FOOTER_LINKS.quickLinks.map((link: FooterQuickLink) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className={`text-[12.5px] transition-colors hover:text-white/65 ${
                      link.highlight ? "text-gold" : "text-white/25"
                    }`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-5">
          <p className="text-white/15 text-[11px] tracking-wide">
            © {currentYear} Olóye Creative Group LLC DBA Olóye Events · NYC & New Jersey
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/oloye.events?igsh=MTBpZ2VrYTd1ODh5ZQ%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-white/30 hover:text-gold transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4.5"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@olye.events?_r=1&_t=ZT-95HEzsWU9cb"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="text-white/30 hover:text-gold transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
              </svg>
            </a>
            <a
              href="mailto:events@oloyeevents.com"
              className="text-[12.5px] text-gold hover:text-gold-light transition-colors tracking-wide"
            >
              events@oloyeevents.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  items,
  highlightIndexes = [],
}: {
  title: string;
  items: string[];
  highlightIndexes?: number[];
}) {
  return (
    <div>
      <h5 className="font-syne text-[9px] font-bold tracking-[0.22em] uppercase text-gold mb-5">
        {title}
      </h5>
      <ul className="space-y-2.5">
        {items.map((item, i) => (
          <li
            key={item}
            className={`text-[12.5px] transition-colors cursor-pointer hover:text-white/65 ${
              highlightIndexes.includes(i) ? "text-gold" : "text-white/25"
            }`}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
